import { vec3, vec4 } from "gl-matrix";
import { Ray } from "../Math/Ray";
import { BoundingBox, IBoundingBoxObject, vectorMax, vectorMin } from '@spi/Common/Util';
import { HeightSpan } from "./HeightSpan";
import { PathNode } from "./PathNode";

export class ConvexShape implements IBoundingBoxObject {

    private _position: vec3;
    private _boundingBox: BoundingBox;
    public spanSet: Set<HeightSpan>;
    public neighbourSet: Set<ConvexShape>;
    public color: vec4;
    public groupReference: string;
    public node: PathNode;

    constructor(public voxelSize: number) {
        this.spanSet = new Set<HeightSpan>();
        this.neighbourSet = new Set<ConvexShape>();

        this.color = vec4.fromValues(Math.random(), Math.random(), Math.random(), 0.5);
    }

    public createHeightGrid(): Float32Array[] {

        // Group by the y-axis
        let spanRowMap = new Map<number, HeightSpan[]>();
        for (let span of this.spanSet) {
            let y = span.spanLocation[1];
            if (!spanRowMap.has(y)) {
                spanRowMap.set(y, []);
            }
            spanRowMap.get(y).push(span);
        }

        let rows: { y: number, spans: HeightSpan[] }[] = [];
        spanRowMap.forEach((spans, y) => {
            rows.push({
                y: y,
                // Sort by the x-axis
                spans: spans.sort((a, b) => a.spanLocation[0] - b.spanLocation[0])
            });
        });

        // Sort by y-axis
        rows.sort((a, b) => a.y - b.y);

        let spanGrid: HeightSpan[][] = [];

        for (let row of rows) {
            spanGrid.push(row.spans);
        }

        let grid: Float32Array[] = [];

        for (let y = 0; y < spanGrid.length; y++) {
            let gridRow: number[] = [];
            
            let rowWidth = spanGrid[y].length;
            for (let x = 0; x < rowWidth; x++) {
                let origin = spanGrid[y][x];

                gridRow.push(this.getVertexHeight(origin, [['n', 'w'], ['w', 'n']]));
                if (x == rowWidth - 1) {
                    gridRow.push(this.getVertexHeight(origin, [['n', 'e'], ['e', 'n']]));
                }

            }

            grid.push(new Float32Array(gridRow));

            if (y == spanGrid.length - 1) {
                let extraRow: number[] = [];
                for (let x = 0; x < rowWidth; x++) {
                    let origin = spanGrid[y][x];

                    extraRow.push(this.getVertexHeight(origin, [['s', 'w'], ['w', 's']]));
                    if (x == rowWidth - 1) {
                        extraRow.push(this.getVertexHeight(origin, [['s', 'e'], ['e', 's']]));
                    }

                }

                grid.push(new Float32Array(extraRow));
            }
        }

        return grid;
    }

    private getVertexHeight(origin: HeightSpan, stepSets: string[][]): number {
        let spans = new Set<HeightSpan>([origin]);

        for (let stepSet of stepSets) {
            let currentSpan = origin;
            for (let step of stepSet) {
                if (currentSpan.compassNeighbours[step]) {
                    currentSpan = currentSpan.compassNeighbours[step];
                    spans.add(currentSpan);
                }
            }
        }

        return Math.max(...[...spans].map(span => span.bottom));
    }

    public addSpan(span: HeightSpan) {
        this.spanSet.add(span);
    }

    public addNeighbour(shape: ConvexShape) {
        if (shape == this) {
            throw new Error(`ConvexShape cannot add neighbour relation to itself`);
        }

        this.neighbourSet.add(shape);
        shape.neighbourSet.add(this);
    }

    getBoundingBox(): BoundingBox {
        if (!this._boundingBox) {
            let bounds = this.getMinMax();
            this._boundingBox = new BoundingBox(bounds.min, bounds.max);
        }
        return this._boundingBox;
    }
    canIntersectRay(): boolean {
        return false;
    }
    intersectsRay(ray: Ray): boolean {
        return false;
    }

    public get position(): vec3 {
        if (!this._position) {
            this._position = this.getBoundingBox().getCenter();
        }

        return this._position;
    }

    getPosition(): vec3 {
        return this.position;
    }

    private getMinMax() {
        let vectorsMin = [];
        this.spanSet.forEach(span => vectorsMin.push([(span.spanLocation[0] + 0) * this.voxelSize, (span.spanLocation[1] + 0) * this.voxelSize, span.bottom]));

        let vectorsMax = [];
        this.spanSet.forEach(span => vectorsMax.push([(span.spanLocation[0] + 1) * this.voxelSize, (span.spanLocation[1] + 1) * this.voxelSize, span.bottom]));

        return {
            min: vectorMin(3, vectorsMin),
            max: vectorMax(3, vectorsMax)
        };
    }
}

